import { useState, useEffect, useRef, useMemo } from "react";
import VideoRecorder from "./VideoRecorder";

export interface Question {
  id: string;
  type: "text" | "checkbox" | "video" | "boolean" | "multiple_choice" | "experience";
  prompt: string;
  options?: string[]; // For checkbox and multiple_choice questions
  multiSelect?: boolean;
  maxDuration?: number; // Max duration for video questions (in seconds)
}

export interface Answer {
  questionId: string;
  type: "text" | "checkbox" | "video" | "boolean" | "multiple_choice" | "experience";
  value: string | string[] | Blob;
}

interface StepQuestionsProps {
  questions: Question[];
  onComplete: (answers: Answer[]) => void;
  onBack: () => void;
  onQuestionProgress?: (index: number) => void;
}

export default function StepQuestions({
  questions,
  onComplete,
  onBack,
  onQuestionProgress,
}: StepQuestionsProps) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [currentAnswer, setCurrentAnswer] = useState<string | string[] | Blob | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  // Auto-focus textarea
  useEffect(() => {
    if (currentQuestion.type === "text" && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [currentQuestionIndex, currentQuestion.type]);

  useEffect(() => {
    onQuestionProgress?.(currentQuestionIndex);
  }, [currentQuestionIndex, onQuestionProgress]);

  // Load existing answer when navigating back
  useEffect(() => {
    const existingAnswer = answers.find((a) => a.questionId === currentQuestion.id);
    if (existingAnswer) {
      setCurrentAnswer(existingAnswer.value);
    } else {
      setCurrentAnswer(null);
    }
  }, [currentQuestionIndex, currentQuestion.id, answers]);

  const hasAnswer = useMemo(() => {
    if (currentAnswer === null) {
      return false;
    }
    if (Array.isArray(currentAnswer)) {
      return currentAnswer.length > 0;
    }
    if (currentQuestion.type === "checkbox" || currentQuestion.type === "multiple_choice") {
      return currentAnswer !== "";
    }
    if (currentQuestion.type === "text") {
      return typeof currentAnswer === "string" && currentAnswer.trim().length > 0;
    }
    return true;
  }, [currentAnswer, currentQuestion.type]);

  const handleNext = () => {
    if (!hasAnswer || currentAnswer === null) return;

    // Save answer
    const newAnswers = answers.filter((a) => a.questionId !== currentQuestion.id);
    newAnswers.push({
      questionId: currentQuestion.id,
      type: currentQuestion.type,
      value: currentAnswer,
    });
    setAnswers(newAnswers);

    if (isLastQuestion) {
      onComplete(newAnswers);
    } else {
      setCurrentQuestionIndex((prev) => prev + 1);
      setCurrentAnswer(null);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    } else {
      onBack();
    }
  };

  return (
    <div className="min-h-full bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Question Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 space-y-6">
          {/* Question Prompt */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {currentQuestion.prompt}
            </h2>
          </div>

          {/* Answer Input */}
          <div className="min-h-[300px]">
            {/* Text Input */}
            {currentQuestion.type === "text" && (
              <textarea
                ref={textareaRef}
                value={typeof currentAnswer === "string" ? currentAnswer : ""}
                onChange={(e) => setCurrentAnswer(e.target.value)}
                placeholder="Type your answer here..."
                className="w-full h-64 px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none text-base"
              />
            )}

            {/* Checkbox Input */}
            {currentQuestion.type === "checkbox" && currentQuestion.options && (
              <div className="space-y-3">
                {currentQuestion.options.map((option) => {
                  const isMulti = currentQuestion.multiSelect;
                  const selectedValues = Array.isArray(currentAnswer)
                    ? currentAnswer
                    : typeof currentAnswer === "string"
                    ? [currentAnswer]
                    : [];
                  const isSelected = isMulti
                    ? selectedValues.includes(option)
                    : currentAnswer === option;

                  const handleClick = () => {
                    if (isMulti) {
                      const nextValues = isSelected
                        ? selectedValues.filter((value) => value !== option)
                        : [...selectedValues, option];
                      setCurrentAnswer(nextValues);
                    } else {
                      setCurrentAnswer(option);
                    }
                  };

                  return (
                    <button
                      key={option}
                      onClick={handleClick}
                      className={`
                        w-full px-6 py-4 rounded-xl border-2 font-semibold text-lg transition-all
                        ${
                          isSelected
                            ? "bg-purple-600 border-purple-600 text-white"
                            : "bg-white border-gray-300 text-gray-700 hover:border-purple-400"
                        }
                      `}
                    >
                      <div className="flex items-center justify-between">
                        <span>{option}</span>
                        {isSelected && (
                          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Multiple Choice Input */}
            {currentQuestion.type === "multiple_choice" && (
              <div className="space-y-3">
                {!currentQuestion.options || currentQuestion.options.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-lg">No options provided</p>
                    <input
                      type="text"
                      value={typeof currentAnswer === "string" ? currentAnswer : ""}
                      onChange={(e) => setCurrentAnswer(e.target.value)}
                      placeholder="Please type your answer..."
                      className="mt-4 w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                    />
                  </div>
                ) : (
                  currentQuestion.options.map((option) => {
                    const isSelected = currentAnswer === option;

                    const handleClick = () => {
                      setCurrentAnswer(option);
                    };

                    return (
                      <button
                        key={option}
                        onClick={handleClick}
                        className="w-full py-3 font-medium text-lg transition-all text-left"
                      >
                        <div className="flex items-center justify-between">
                          <span className={isSelected ? "text-blue-600" : "text-gray-700"}>{option}</span>
                          {isSelected && (
                            <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            )}

            {/* Video Input */}
            {currentQuestion.type === "video" && (
              <VideoRecorder
                maxDuration={currentQuestion.maxDuration}
                onVideoReady={(blob, url) => {
                  setCurrentAnswer(blob);
                  console.log("Video recorded:", { blob, url });
                }}
              />
            )}
          </div>

          {/* Navigation */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={handlePrevious}
              className="px-6 h-12 rounded-xl border-2 border-gray-300 hover:border-gray-400 bg-white text-gray-700 font-semibold transition-colors"
            >
              ← Back
            </button>
            <button
              onClick={handleNext}
              disabled={!hasAnswer}
              className={`
                flex-1 h-12 rounded-xl font-bold transition-all
                ${
                  hasAnswer
                    ? "bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }
              `}
            >
              {isLastQuestion ? "Finish Application" : "Next Question"} →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


